/*
 * The quantity stepper. Two +/- buttons over a number input, in a buy box and
 * on a cart line.
 *
 * The stepper is only ever an enhancement of the input beneath it, and on a
 * cart line that input is written to Shopify by the cart controller in
 * assets/cart-lines.js, which arrives lazily. Until it is listening, a press
 * would move the field and persist nothing -- so on a cart line the stepper
 * waits for it, and the line keeps the native input and the visible Update
 * button that still reach Shopify. In a buy box the field posts with its own
 * form, so there is nothing to wait for. Behaviour: bin/test-quantity-input.mjs.
 */

(function () {
  "use strict";

  var CART_LIST = "faubourg-cart-items";

  function controllerReady() {
    return Boolean(window.Faubourg && window.Faubourg.cartLinesInstalled);
  }

  class FaubourgQuantityInput extends HTMLElement {
    connectedCallback() {
      this.input = this.querySelector("input");
      this.addEventListener("click", this.handleClick);
      this.addEventListener("input", this.reflectBounds);
      this.addEventListener("change", this.reflectBounds);
      this.reflectEnhancement();
      this.reflectBounds();
    }

    disconnectedCallback() {
      this.removeEventListener("click", this.handleClick);
      this.removeEventListener("input", this.reflectBounds);
      this.removeEventListener("change", this.reflectBounds);
    }

    // Drawn from the field each time it is said to have moved: by the shopper,
    // by a press here, or by a script putting a quantity back, which says so
    // with an input event (cart-lines.js, variant-picker.js). aria-disabled,
    // not disabled: the button pressed down to the minimum keeps focus.
    reflectBounds = () => {
      if (!this.input || this.input.disabled) return;
      const value = Number(this.input.value);
      const min = this.input.min === "" ? -Infinity : Number(this.input.min);
      const max = this.input.max === "" || this.input.max == null ? Infinity : Number(this.input.max);
      this.markBound("minus", value <= min);
      this.markBound("plus", value >= max);
    };

    markBound(name, atBound) {
      const button = this.querySelector('[name="' + name + '"]');
      if (!button) return;
      if (atBound) button.setAttribute("aria-disabled", "true");
      else button.removeAttribute("aria-disabled");
    }

    // The attribute is what the stylesheet reads: exactly one of the stepper
    // and the line's Update button is offered, never both and never neither.
    reflectEnhancement() {
      if (this.closest(CART_LIST) && !controllerReady()) {
        this.removeAttribute("data-enhanced");
        return;
      }
      this.setAttribute("data-enhanced", "");
    }

    handleClick = (event) => {
      // A press that cannot be saved is not taken: the field keeps the
      // quantity the server confirmed, and nothing is announced as done.
      if (!this.hasAttribute("data-enhanced")) return;

      const button = event.target.closest("button");
      if (!button) return;

      // Read from the field, not from the button's mark: at a bound the step
      // moves nothing, and nothing is announced.
      const before = this.input.value;
      if (button.name === "plus") this.input.stepUp();
      if (button.name === "minus") this.input.stepDown();
      if (this.input.value === before) return;

      this.input.dispatchEvent(new Event("change", { bubbles: true }));
    };
  }

  if (!customElements.get("faubourg-quantity-input")) {
    customElements.define("faubourg-quantity-input", FaubourgQuantityInput);
  }

  // Cart lines connect long before the controller does, so they are told once
  // it has. A line rendered afterwards reads the flag as it connects instead.
  document.addEventListener("cart:lines-ready", function () {
    var steppers = document.querySelectorAll("faubourg-quantity-input");
    for (var index = 0; index < steppers.length; index += 1) {
      if (typeof steppers[index].reflectEnhancement === "function") {
        steppers[index].reflectEnhancement();
      }
    }
  });
})();
